import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Play, ArrowLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const VideosPage = ({ onBack }) => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/videos`);
      setVideos(response.data);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
  };

  const extractYouTubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <p className="text-slate-600">Loading videos...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        {onBack && (
          <Button
            variant="ghost"
            onClick={onBack}
            className="mb-6 hover:bg-slate-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        )}

        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-purple-700 via-blue-700 to-purple-700 bg-clip-text text-transparent">
            Instructional Videos
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Educational content by Empress Ninti El Bey
          </p>
          <p className="text-base text-slate-500 mt-2">
            Master the Reclaimation process with comprehensive video tutorials
          </p>
        </div>

        {/* Video Grid */}
        {videos.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-600 text-lg">No videos available yet</p>
            <p className="text-slate-500 text-sm mt-2">Check back soon for instructional content</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video) => {
              const youtubeId = extractYouTubeId(video.youtubeUrl);
              return (
                <Card
                  key={video.id}
                  className="cursor-pointer transition-all duration-300 hover:shadow-xl hover:scale-105 border-2 border-slate-200 hover:border-purple-400"
                  onClick={() => handleVideoClick(video)}
                >
                  <div className="relative">
                    {youtubeId ? (
                      <img
                        src={`https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`}
                        alt={video.title}
                        className="w-full h-48 object-cover rounded-t-lg"
                        onError={(e) => {
                          e.target.src = `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
                        }}
                      />
                    ) : (
                      <div className="w-full h-48 bg-gradient-to-br from-purple-400 to-blue-400 flex items-center justify-center rounded-t-lg">
                        <Play className="w-16 h-16 text-white" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center rounded-t-lg hover:bg-opacity-40 transition-all">
                      <div className="w-16 h-16 rounded-full bg-white bg-opacity-90 flex items-center justify-center">
                        <Play className="w-8 h-8 text-purple-700 ml-1" fill="currentColor" />
                      </div>
                    </div>
                  </div>
                  <CardContent className="pt-4">
                    <h3 className="font-semibold text-lg text-slate-900 mb-2 line-clamp-2">
                      {video.title}
                    </h3>
                    <p className="text-slate-600 text-sm line-clamp-3">
                      {video.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center border-t-2 border-slate-200 pt-8">
          <p className="text-slate-600 mb-2">
            For additional resources and learning materials
          </p>
          <a
            href="https://knowledgebase.live"
            target="_blank"
            rel="noopener noreferrer"
            className="text-purple-700 hover:text-purple-900 font-semibold underline transition-colors"
          >
            Visit knowledgebase.live by Empress Ninti El Bey
          </a>
        </div>
      </div>

      {/* Video Player Modal */}
      <Dialog open={selectedVideo !== null} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">{selectedVideo?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedVideo && extractYouTubeId(selectedVideo.youtubeUrl) ? (
              <div className="relative" style={{ paddingBottom: '56.25%', height: 0 }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                  src={`https://www.youtube.com/embed/${extractYouTubeId(selectedVideo.youtubeUrl)}`}
                  title={selectedVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            ) : (
              <div className="w-full h-96 bg-slate-200 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <Play className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600 font-semibold">Video Not Available</p>
                  <p className="text-slate-500 text-sm mt-2">This video link may be invalid</p>
                </div>
              </div>
            )}
            <div>
              <p className="text-slate-700 leading-relaxed">
                {selectedVideo?.description}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VideosPage;